type PropertyNumericDisplayOptions = {
  value: number
  suffix?: string
  autoWeightUnit?: boolean
  locale?: string
  maximumFractionDigits?: number
  minimumFractionDigits?: number
}

const DEFAULT_PROPERTY_DISPLAY_LOCALE = 'fr-FR'

const normalizeSuffix = (suffix: string | undefined): string | undefined => {
  const trimmed = suffix?.trim()
  return trimmed ? trimmed : undefined
}

export const resolvePropertyDisplayMaximumFractionDigits = (
  value: number,
): number => {
  return Number.isInteger(value) ? 0 : 1
}

const formatNumericValue = ({
  value,
  locale,
  maximumFractionDigits,
  minimumFractionDigits,
}: Pick<
  PropertyNumericDisplayOptions,
  'value' | 'locale' | 'maximumFractionDigits' | 'minimumFractionDigits'
>) => {
  if (
    maximumFractionDigits === undefined &&
    minimumFractionDigits === undefined
  ) {
    return value.toLocaleString(locale)
  }

  return new Intl.NumberFormat(locale, {
    maximumFractionDigits:
      maximumFractionDigits ?? Math.max(minimumFractionDigits ?? 0, 0),
    minimumFractionDigits,
  }).format(value)
}

export const resolvePropertyDisplayUnitLabel = ({
  suffix,
  autoWeightUnit,
}: Pick<
  PropertyNumericDisplayOptions,
  'suffix' | 'autoWeightUnit'
>): string => {
  const normalizedSuffix = normalizeSuffix(suffix)

  if (autoWeightUnit) {
    return normalizedSuffix ? `kg/t ${normalizedSuffix}` : 'kg/t'
  }

  return normalizedSuffix ?? ''
}

export const formatPropertyNumericDisplayValue = ({
  value,
  suffix,
  autoWeightUnit,
  locale = DEFAULT_PROPERTY_DISPLAY_LOCALE,
  maximumFractionDigits,
  minimumFractionDigits,
}: PropertyNumericDisplayOptions): string => {
  const normalizedSuffix = normalizeSuffix(suffix)

  if (!autoWeightUnit) {
    const formattedValue = formatNumericValue({
      value,
      locale,
      maximumFractionDigits,
      minimumFractionDigits,
    })

    return normalizedSuffix
      ? `${formattedValue} ${normalizedSuffix}`
      : formattedValue
  }

  const useTonUnit = Math.abs(value) >= 1000
  const formattedValue = formatNumericValue({
    value: useTonUnit ? value / 1000 : value,
    locale,
    maximumFractionDigits: useTonUnit ? 1 : maximumFractionDigits,
    minimumFractionDigits:
      useTonUnit && minimumFractionDigits !== undefined
        ? Math.min(minimumFractionDigits, 1)
        : minimumFractionDigits,
  })

  return [formattedValue, useTonUnit ? 't' : 'kg', normalizedSuffix]
    .filter(Boolean)
    .join(' ')
}
